/**
 * Display Helper Utilities
 * Provides fallback values for missing data to prevent UI breaks
 */

// Default fallback values for different field types
const FALLBACKS = {
  text: 'Not provided',
  bio: 'Bio coming soon',
  name: 'Unknown',
  location: 'Location not specified',
  range: 'Undisclosed',
  number: 0,
  percentage: 0,
  list: [],
  date: null,
  currency: 'USD',
  image: '/assets/avatar-placeholder.svg',
};

/**
 * Display a value with a fallback if missing/empty
 * @param {any} value - The value to display
 * @param {string} fallbackType - Type of fallback ('text', 'bio', 'name', 'location', 'range', 'number', 'list')
 * @param {any} customFallback - Optional custom fallback value
 * @returns {any} The value or fallback
 */
export const displayWithFallback = (value, fallbackType = 'text', customFallback = null) => {
  // Check for null, undefined, empty string, empty array
  const isEmpty = 
    value === null || 
    value === undefined || 
    value === '' || 
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

  if (isEmpty) {
    return customFallback !== null ? customFallback : FALLBACKS[fallbackType] || FALLBACKS.text;
  }

  return value;
};

/**
 * Format currency range with fallback
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {string} currency - Currency code
 * @returns {string} Formatted range or fallback
 */
export const formatCurrencyRange = (min, max, currency = 'USD') => {
  if (!min && !max) return FALLBACKS.range;
  
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  const curr = currency || 'USD';
  
  if (min && max) {
    return `${curr} ${formatNumber(min)} - ${formatNumber(max)}`;
  }
  if (min) return `${curr} ${formatNumber(min)}+`;
  if (max) return `Up to ${curr} ${formatNumber(max)}`;
  
  return FALLBACKS.range;
};

/**
 * Format location from city and country
 * @param {string} city - City name
 * @param {string} country - Country name
 * @returns {string} Formatted location or fallback
 */
export const formatLocation = (city, country) => {
  if (!city && !country) return FALLBACKS.location;
  if (city && country) return `${city}, ${country}`;
  return city || country || FALLBACKS.location;
};

/**
 * Get initials from name
 * @param {string} name - Full name
 * @returns {string} Initials (max 2 characters)
 */
export const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Format date with fallback
 * @param {string|Date} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date or fallback
 */
export const formatDate = (date, options = { month: 'short', day: 'numeric', year: 'numeric' }) => {
  if (!date) return 'Date not available';
  try {
    return new Date(date).toLocaleDateString('en-US', options);
  } catch {
    return 'Invalid date';
  }
};

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';
  
  try {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return formatDate(date, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

/**
 * Safely get nested property value
 * @param {object} obj - Object to search
 * @param {string} path - Dot-notation path (e.g., 'profile.name')
 * @param {any} fallback - Fallback value
 * @returns {any} Value or fallback
 */
export const getNestedValue = (obj, path, fallback = null) => {
  if (!obj || !path) return fallback;
  
  const value = path.split('.').reduce((acc, part) => {
    return acc && acc[part] !== undefined ? acc[part] : undefined;
  }, obj);
  
  return value !== undefined ? value : fallback;
};

/**
 * Format list as comma-separated string with fallback
 * @param {Array} list - Array of items
 * @param {number} maxItems - Maximum items to show before truncating
 * @returns {string} Formatted string
 */
export const formatList = (list, maxItems = 3) => {
  if (!Array.isArray(list) || list.length === 0) return FALLBACKS.text;
  
  if (list.length <= maxItems) {
    return list.join(', ');
  }
  
  return `${list.slice(0, maxItems).join(', ')} +${list.length - maxItems} more`;
};

/**
 * Get avatar URL with fallback
 * @param {string} url - Avatar URL
 * @param {string} name - User name for generating placeholder
 * @returns {string} Avatar URL or placeholder
 */
export const getAvatarUrl = (url, name) => {
  if (url && url.trim()) return url;
  // Return a placeholder that can be handled by UI
  return null;
};

export default {
  displayWithFallback,
  formatCurrencyRange,
  formatLocation,
  getInitials,
  formatDate,
  formatRelativeTime,
  getNestedValue,
  formatList,
  getAvatarUrl,
  FALLBACKS,
};
